/**
 * `<OrderbookLadder>` — **fixed rows, mutated directly, never reconciled.**
 *
 * Plan §2.3 is a hard invariant and this file is where it lives. The row set is
 * allocated once at a fixed size and never grows, shrinks or reorders; a book
 * frame changes the `textContent` of some cells and the `width` of some bars,
 * and React is not involved. At 20 Hz through the final three seconds, with
 * 20–40 level changes a frame, mapping the ladder to elements is the difference
 * between 60 fps and a visible stutter on the five seconds that matter.
 *
 * **This is why the ladder does not use `<NumCell>`.** `<NumCell>` implements
 * flash-on-rendered-change correctly and costs one forced reflow per changed
 * cell, which is right for a positions table and wrong for fifty rows at 131
 * f/s. The flash here is the same *rule* — compare the rendered string, flash
 * only when it differs — applied without the reflow: the class is toggled off
 * and on across an `offsetWidth` read only for cells that actually changed, and
 * in a normal frame that is a handful, not fifty.
 *
 * **The DOWN ladder is not fetched.** Per D1 the engine normalises every order
 * onto the UP leg, so there is one ladder per market and DOWN is the exact
 * mirror — `bid s@p` becomes `ask s@(100−p)`, reversed. `BookStore.downLadder()`
 * implements it once, so the two can never disagree.
 *
 * **A stale book renders, dimmed, and never blanks** (§4.1). A dropped socket,
 * a `seq` gap or a checksum mismatch degrades the panel to "last known good
 * behind a `STALE` pill"; blanking it would replace a slightly old book with no
 * book, which is strictly worse information.
 *
 * ## Twenty-one slots, and what sits in each
 *
 * Ten asks, the spread and ten bids — but as twenty-one identical slots rather
 * than three blocks, because while the pointer is over the book the spread is
 * not always the middle row. `domain/book-layout.ts` decides what each slot
 * holds and is the whole argument: **the row under the pointer stays still**,
 * and the book is laid out around it until the pointer leaves. Every slot is
 * one height, so holding a slot holds its pixel.
 *
 * A level slot carries `data-row` and the spread slot `data-mid`, written as
 * the layout moves the spread — so outside a hover the DOM still reads ten
 * `[data-row]`, the mid and ten `[data-row]`, which is what the browser suite
 * counts.
 *
 * ## Click to trade, and the account's own orders
 *
 * With `clickToTrade` a click on an ask or bid row calls `onTrade` with that
 * row's side and price, in the book's own outcome's terms; the caller decides
 * what it means (`<BookTradingLadder>`). A touch never trades — a tap meant to
 * scroll the panel must not place an order.
 *
 * Every row where the account has a resting order carries a mark beside its
 * price, read from the same `ownBookFrom` projection the pro ladder paints.
 * Anywhere over that row the tooltip shows how much has filled — with click to
 * trade on, above the line a click would place — and the mark becomes a ✕.
 * Only a click on the ✕ calls `onCancel`; the rest of the row trades, or does
 * nothing with the switch off. Cancelling needs no switch: a cancel carries no
 * signature and no money, and pulling a resting order is never gated.
 *
 * The tooltip is **one** `position: fixed` element set imperatively, at the
 * pointer, and it follows it — outside the element that carries the stale
 * styling, because `saturate` is a CSS `filter` and a filtered ancestor becomes
 * a fixed child's containing block, which would clip it inside the scroll body.
 */

import { memo, useCallback, useEffect, useMemo, useRef } from 'react'
import { useRuntime } from '@/app/use-runtime'
import { useStoreVersion } from '@/app/use-store'
import { Pill } from '@/components/primitives'
import { cx } from '@/lib/cx'
import { formatFixed, parseFixed, SHARES_DP, type Outcome } from '@/lib/decimal'
import { formatShares } from '@/lib/format'
import { anchorAt, layoutSlots, slotCount, type Anchor, type Slot } from '@/domain/book-layout'
import {
  ownBookFrom,
  ownLevelFor,
  type LadderSide,
  type OwnBook,
  type OwnLevel,
} from '@/domain/pro-ladder'
import type { BookLevelRow } from '@/stores/book'
import { ExternalStore } from '@/stores/store'

/** Levels per side outside a hover. Fixed for the life of the panel — that is the point. */
export const LADDER_ROWS = 10

const SLOTS = slotCount(LADDER_ROWS)

/**
 * The tooltip opens at the pointer: this far to its right, centred on it, so
 * the cursor never covers the words. With no room to the right of the pointer
 * it opens to its left; `TIP_GAP_PX` keeps it off the window's edges.
 */
const TIP_CURSOR_PX = 16
const TIP_GAP_PX = 6

export interface OrderbookLadderProps {
  marketId: string | null
  /** `UP` shows the real ladder; `DOWN` shows the client-side mirror. */
  outcome?: Outcome
  /** Copy for the pre-open case: `opens in {n}s`, **never** `no liquidity`. */
  opensInSeconds?: number | null
  /** A click on a level places an order there. Off, the book is read-only. */
  clickToTrade?: boolean
  /** A click on an ask or bid row, in the book's own terms. */
  onTrade?: (side: LadderSide, cents: number, row: HTMLElement) => void
  /** A click on the ✕ of the account's own order on a row, in the book's own terms. */
  onCancel?: (side: LadderSide, cents: number, row: HTMLElement) => void
  /** What a click on a row would do, for the tooltip. `null` shows nothing. */
  describe?: (side: LadderSide, cents: number) => string | null
  className?: string
}

const EMPTY = new ExternalStore<never[]>([])
const NO_OWN: OwnBook = { bid: new Map(), ask: new Map() }

export const OrderbookLadder = memo(function OrderbookLadder({
  marketId,
  outcome = 'UP',
  opensInSeconds = null,
  clickToTrade = false,
  onTrade,
  onCancel,
  describe,
  className,
}: OrderbookLadderProps) {
  const runtime = useRuntime()
  const stores = runtime.stores
  const bookStore = marketId ? stores?.bookFor(marketId) : undefined
  // Three signals, no data. Each says "look again"; the effect below reads the
  // stores' state directly and mutates the DOM.
  const bookVersion = useStoreVersion((bookStore?.store ?? EMPTY) as ExternalStore<unknown>)
  const ordersVersion = useStoreVersion((stores?.orders.store ?? EMPTY) as ExternalStore<unknown>)
  const pendingVersion = useStoreVersion(
    (stores?.pendingOrders.store ?? EMPTY) as ExternalStore<unknown>,
  )

  const rootRef = useRef<HTMLDivElement>(null)
  const slotsRef = useRef<HTMLDivElement>(null)
  const tipRef = useRef<HTMLDivElement>(null)
  /**
   * The slot handles, resolved from the DOM inside the effect.
   *
   * Deliberately *not* registered by the slot components into a parent array:
   * a child writing into a parent's ref is a render-phase mutation, and the
   * React Compiler rules reject it. Walking the container's own children once is
   * simpler and has no ordering question.
   */
  const handles = useRef<SlotHandles[] | null>(null)
  /** What each slot shows, as of the last paint. A click reads this. */
  const layout = useRef<Slot[]>([])
  /** The entry held still under the pointer, and the outcome it was taken in. */
  const anchor = useRef<{ at: Anchor; outcome: Outcome } | null>(null)
  const hover = useRef<{ row: number | null; onMark: boolean; pointer: string; x: number; y: number }>({
    row: null,
    onMark: false,
    pointer: '',
    x: 0,
    y: 0,
  })
  /** The own-order projection, and the versions it was built from. */
  const own = useRef<{ book: OwnBook; ordersV: number; pendingV: number; marketId: string | null }>({
    book: NO_OWN,
    ordersV: -1,
    pendingV: -1,
    marketId: null,
  })
  /**
   * The props the pointer handlers read. Refreshed after every render rather
   * than listed in a dependency array, so the handlers keep one identity and a
   * new `describe` from a 4 Hz caller costs nothing here.
   */
  const props = useRef({ clickToTrade, onTrade, onCancel, describe })
  useEffect(() => {
    props.current = { clickToTrade, onTrade, onCancel, describe }
  })

  /**
   * Show, fill and place the tooltip for the slot under the pointer — or hide
   * it.
   *
   * `measure` is the pointer's: the tooltip follows the pointer, so only a
   * pointer move places it, and a book frame refreshes the words and leaves the
   * position alone unless the tooltip is only now appearing. Measuring on every
   * frame would be a forced layout twenty times a second for a box that has not
   * moved.
   */
  const refreshTip = useCallback((measure: boolean) => {
    const tip = tipRef.current
    const root = rootRef.current
    const rows = handles.current
    const row = hover.current.row
    if (!tip) return
    if (!root || !rows || row === null) return void hideTip(tip)
    const slot = layout.current[row]
    const handle = rows[row]
    if (!slot || !handle || (slot.kind !== 'ask' && slot.kind !== 'bid') || slot.cents === null) {
      return void hideTip(tip)
    }

    // A row with the account's order shows the order card anywhere on it, and
    // under the card the line a click on the row would place — except on the
    // mark, where a click cancels rather than trades.
    const { clickToTrade: on, describe: say } = props.current
    const text = on && say && !(hover.current.onMark && handle.own) ? say(slot.kind, slot.cents) : null
    if (!handle.own && !text) return void hideTip(tip)
    fillTip(tip, handle.own, text)

    const appearing = tip.hidden
    tip.hidden = false
    if (!measure && !appearing) return
    const { x, y } = hover.current
    const viewport = document.documentElement
    const width = tip.offsetWidth
    const half = tip.offsetHeight / 2
    const right = x + TIP_CURSOR_PX
    tip.style.left = `${
      right + width + TIP_GAP_PX <= viewport.clientWidth ? right : Math.max(TIP_GAP_PX, x - TIP_CURSOR_PX - width)
    }px`
    tip.style.top = `${Math.min(Math.max(y, half + TIP_GAP_PX), viewport.clientHeight - half - TIP_GAP_PX)}px`
  }, [])

  const paint = useCallback(() => {
    if (!handles.current) {
      const resolved = handlesIn(slotsRef.current)
      if (resolved.length !== SLOTS) return
      handles.current = resolved
    }
    const rows = handles.current

    // --- own orders, rebuilt only when they actually moved ------------------
    if (
      stores &&
      (own.current.ordersV !== stores.orders.store.version ||
        own.current.pendingV !== stores.pendingOrders.store.version ||
        own.current.marketId !== marketId)
    ) {
      own.current = {
        // Read `byId` rather than `list()`, which allocates and is a
        // render-time call rather than a per-frame one.
        book: ownBookFrom(
          Array.from(stores.orders.store.state.byId.values()) as Record<string, unknown>[],
          Array.from(stores.pendingOrders.store.state.byId.values()),
          marketId,
        ),
        ordersV: stores.orders.store.version,
        pendingV: stores.pendingOrders.store.version,
        marketId,
      }
    }

    // --- the book -------------------------------------------------------------
    // The mirror is computed here rather than stored, because it is a pure
    // function of the UP book and storing it would be a second ladder to keep
    // in sync.
    const state = bookStore?.state ?? null
    const ladder =
      !state ? { bids: [] as BookLevelRow[], asks: [] as BookLevelRow[] }
      : outcome === 'DOWN' && bookStore ? bookStore.downLadder()
      : { bids: state.bids, asks: state.asks }
    const asks = indexSide(ladder.asks)
    const bids = indexSide(ladder.bids)

    // An anchor taken in the other outcome's book points at a price that means
    // something else here; the switch itself is a click in the ticket, so the
    // pointer is not on the book anyway.
    if (anchor.current && anchor.current.outcome !== outcome) anchor.current = null
    const slots = layoutSlots(asks.cents, bids.cents, LADDER_ROWS, anchor.current?.at ?? null)
    layout.current = slots

    // The deepest level shown on each side carries that side's whole shown
    // depth, so it is the bar's denominator.
    let maxAsk = 0n
    let maxBid = 0n
    for (const slot of slots) {
      if (slot.cents === null || slot.ghost) continue
      if (slot.kind === 'ask') maxAsk = max(maxAsk, asks.total.get(slot.cents) ?? 0n)
      if (slot.kind === 'bid') maxBid = max(maxBid, bids.total.get(slot.cents) ?? 0n)
    }

    const bestBid = ladder.bids[0]?.price_cents
    const bestAsk = ladder.asks[0]?.price_cents
    const spread = bestBid === undefined || bestAsk === undefined ? '—' : `${bestAsk - bestBid}¢ spread`

    for (let i = 0; i < rows.length; i++) {
      const handle = rows[i]
      const slot = slots[i]
      if (!handle || !slot) continue
      paintSlot(handle, slot, {
        side: slot.kind === 'ask' ? asks : bids,
        maxTotal: slot.kind === 'ask' ? maxAsk : maxBid,
        spread,
        own: own.current.book,
        outcome,
      })
    }

    refreshTip(false)
  }, [bookStore, stores, outcome, marketId, refreshTip])

  useEffect(() => {
    void bookVersion
    void ordersVersion
    void pendingVersion
    paint()
  }, [paint, bookVersion, ordersVersion, pendingVersion])

  /**
   * The pointer, over the slots.
   *
   * A mouse over a slot anchors it — at the layout it is showing, so taking the
   * anchor moves nothing. Moving to another slot re-anchors there: the rows the
   * pointer left may then settle, the row it is on does not move. A touch never
   * anchors, because a touch has no hover.
   */
  const onPointerMove = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      hover.current.pointer = event.pointerType
      if (event.pointerType !== 'mouse') return
      hover.current.x = event.clientX
      hover.current.y = event.clientY
      const target = event.target as HTMLElement
      const slotEl = target.closest<HTMLElement>('[data-slot]')
      if (!slotEl) return
      const row = Number(slotEl.dataset.slot)
      if (hover.current.row !== row) {
        hover.current.row = row
        const at = anchorAt(layout.current, row)
        anchor.current = at ? { at, outcome } : null
      }
      hover.current.onMark = target.closest('[data-cell=mark]') !== null
      refreshTip(true)
    },
    [outcome, refreshTip],
  )

  const onPointerLeave = useCallback(() => {
    hover.current.row = null
    hover.current.onMark = false
    anchor.current = null
    // Back to the normal layout now, not at the next frame: on a quiet book
    // that could be seconds away.
    paint()
  }, [paint])

  const onPointerDown = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    hover.current.pointer = event.pointerType
  }, [])

  const onClick = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement
    const slotEl = target.closest<HTMLElement>('[data-slot]')
    if (!slotEl || slotEl.dataset.busy) return
    const slot = layout.current[Number(slotEl.dataset.slot)]
    if (!slot || (slot.kind !== 'ask' && slot.kind !== 'bid') || slot.cents === null) return

    const mark = target.closest<HTMLElement>('[data-cell=mark]')
    if (mark) {
      if (!mark.hidden) props.current.onCancel?.(slot.kind, slot.cents, slotEl)
      return
    }
    const { clickToTrade: on, onTrade: trade } = props.current
    if (!on || !trade || hover.current.pointer === 'touch') return
    trade(slot.kind, slot.cents, slotEl)
  }, [])

  const slotIndexes = useMemo(() => Array.from({ length: SLOTS }, (_, i) => i), [])
  const stale = bookStore?.state.stale ?? false
  const empty = (bookStore?.state.bids.length ?? 0) === 0 && (bookStore?.state.asks.length ?? 0) === 0

  return (
    <div
      ref={rootRef}
      data-click={clickToTrade ? 'on' : 'off'}
      data-outcome={outcome}
      className={cx('group/book ud-ladder relative flex min-h-0 flex-col', className)}
    >
      <div
        data-stale={stale ? 'true' : undefined}
        className="relative flex min-h-0 flex-col data-[stale=true]:opacity-55 data-[stale=true]:saturate-50"
      >
        <div className="flex items-center px-2 py-1 text-micro text-text-tertiary">
          <span aria-hidden="true" className="w-4 shrink-0" />
          <span className="w-8 shrink-0">Price</span>
          <span className="flex-1 pr-2 text-right">Size</span>
          <span className="w-24 shrink-0 text-right">Total</span>
          {stale ?
            <Pill tone="warning" className="ml-2">
              STALE
            </Pill>
          : null}
        </div>

        <div
          ref={slotsRef}
          onPointerMove={onPointerMove}
          onPointerLeave={onPointerLeave}
          onPointerDown={onPointerDown}
          onClick={onClick}
          className="flex flex-col"
        >
          {slotIndexes.map((i) => (
            <LadderSlot key={i} index={i} />
          ))}
        </div>

        {/* `opens in {n}s`, never `no liquidity` (docs/07 §5). A market in
            `FIXING` has no book because it is not open yet, which is a fact
            about the clock and not about the venue's depth. */}
        {empty ?
          <div className="pointer-events-none absolute inset-0 grid place-items-center">
            <span className="text-body-sm text-text-tertiary">
              {opensInSeconds === null ?
                'no trades yet'
              : `opens in ${Math.max(0, Math.ceil(opensInSeconds))}s`}
            </span>
          </div>
        : null}
      </div>

      <div
        ref={tipRef}
        role="tooltip"
        hidden
        data-testid="book-tip"
        className={cx(
          'group/tip pointer-events-none fixed z-40 w-max max-w-[calc(240*var(--ud-u))] -translate-y-1/2',
          'rounded-md border border-border-default bg-bg-raised px-1_5 py-1 text-caption text-text-primary shadow-popover',
        )}
      >
        <div
          data-tip="order"
          className="hidden min-w-[calc(160*var(--ud-u))] group-data-[face=both]/tip:block group-data-[face=order]/tip:block"
        >
          <div className="flex items-baseline justify-between gap-3">
            <span className="font-semibold">Filled</span>
            <span data-tip="filled" className="ud-num" />
          </div>
          <div className="mt-1 h-1 overflow-hidden rounded-full bg-bg-inset">
            <div data-tip="bar" className="h-full w-0 bg-accent-solid" />
          </div>
          <p data-tip="remaining" className="ud-num mt-1 text-text-secondary" />
        </div>
        <p
          data-tip="action"
          className={cx(
            'hidden group-data-[face=action]/tip:block group-data-[face=both]/tip:block',
            'group-data-[face=both]/tip:mt-1_5 group-data-[face=both]/tip:border-t group-data-[face=both]/tip:border-border-subtle group-data-[face=both]/tip:pt-1_5',
          )}
        />
      </div>
    </div>
  )
})

// ---------------------------------------------------------------------------

interface SlotHandles {
  root: HTMLElement
  mark: HTMLElement
  price: HTMLElement
  size: HTMLElement
  total: HTMLElement
  bar: HTMLElement
  mid: HTMLElement
  /** The last values actually painted, **one per cell** — a slot holds whatever
   *  the layout puts in it, and a single marker cannot speak for every cell. */
  lastKind: string
  lastPrice: string
  lastSize: string
  lastSizeMicro: bigint
  lastTotal: string
  lastWidth: string
  lastMid: string
  lastMark: string
  /** The account's own level on this slot, for the order card. */
  own: OwnLevel | undefined
}

/**
 * One slot. Rendered by React exactly once; everything after that is
 * `textContent`, `style.width` and data attributes written by the paint.
 *
 * Every slot can be an ask, a bid, the spread or empty, so every slot carries
 * every cell, and its colours come from its own `data-kind` through the
 * `group/slot` variants — written out as literals, because Tailwind generates
 * only the classes it can read in the source (`<Countdown>`'s note).
 */
const LadderSlot = memo(function LadderSlot({ index }: { index: number }) {
  return (
    <div
      data-slot={index}
      data-kind="empty"
      className={cx(
        'group/slot ud-num relative flex h-[var(--ud-row-height-dense)] items-center px-2 text-body-sm',
        'border-border-subtle data-[kind=spread]:border-y data-[kind=spread]:bg-bg-inset',
        'data-[kind=ask]:hover:bg-bg-hover data-[kind=bid]:hover:bg-bg-hover',
        'group-data-[click=on]/book:data-[kind=ask]:cursor-pointer group-data-[click=on]/book:data-[kind=bid]:cursor-pointer',
        'data-[busy]:opacity-50',
      )}
    >
      {/* The depth bar is an absolutely positioned background growing from the
          right, behind the text — never a separate element in the flow
          (components.md §3). */}
      <span
        aria-hidden="true"
        data-cell="bar"
        className={cx(
          'absolute inset-y-0 right-0 w-0',
          'group-data-[kind=bid]/slot:bg-[var(--ud-color-up-depth)] group-data-[kind=ask]/slot:bg-[var(--ud-color-down-depth)]',
        )}
      />
      {/* The mark is nearly the row's height so the ✕ is an easy target: it
          sits right beside the price, where a slightly wide click trades. */}
      <span className="relative grid w-5 shrink-0 place-items-center">
        <button
          type="button"
          tabIndex={-1}
          hidden
          data-cell="mark"
          className={cx(
            'grid size-5 place-items-center rounded-full text-accent-text max-md:hidden',
            'hover:text-text-primary data-[pending=true]:opacity-60',
          )}
        >
          <ClockIcon />
          <CrossIcon />
        </button>
      </span>
      <span
        data-cell="price"
        className="relative w-8 shrink-0 text-left group-data-[kind=ask]/slot:text-down-text group-data-[kind=bid]/slot:text-up-text"
      />
      <span data-cell="size" className="relative min-w-0 flex-1 truncate pr-2 text-right text-text-primary" />
      <span
        data-cell="total"
        // `w-24` rather than `w-20`: cumulative depth is grouped now, and a
        // comma is a character the widest realistic total did not have room
        // for. The size column is `flex-1` and gives it back.
        className="relative w-24 shrink-0 truncate text-right text-text-tertiary"
      />
      <span
        data-cell="mid"
        className="pointer-events-none absolute inset-0 grid place-items-center text-caption text-text-secondary"
      />
    </div>
  )
})

/** The mark on a row with the account's order: a clock, becoming ✕ while the row is hovered. */
function ClockIcon() {
  return (
    <svg viewBox="0 0 16 16" className="size-4 group-hover/slot:hidden" fill="none" aria-hidden="true">
      <circle cx="8" cy="8" r="6.25" stroke="currentColor" strokeWidth="1.5" />
      <path d="M8 4.75V8l2.25 1.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  )
}

function CrossIcon() {
  return (
    <svg viewBox="0 0 16 16" className="hidden size-4 group-hover/slot:block" fill="none" aria-hidden="true">
      <circle cx="8" cy="8" r="7" fill="currentColor" opacity="0.2" />
      <path d="M5.5 5.5l5 5M10.5 5.5l-5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  )
}

/** Resolve the fixed slots out of their container, once. */
function handlesIn(container: HTMLElement | null): SlotHandles[] {
  if (!container) return []
  const out: SlotHandles[] = []
  for (const root of Array.from(container.querySelectorAll<HTMLElement>('[data-slot]'))) {
    const cell = (name: string) => root.querySelector<HTMLElement>(`[data-cell=${name}]`)
    const [mark, price, size, total, bar, mid] = ['mark', 'price', 'size', 'total', 'bar', 'mid'].map(cell)
    if (!mark || !price || !size || !total || !bar || !mid) continue
    out.push({
      root,
      mark,
      price,
      size,
      total,
      bar,
      mid,
      lastKind: '',
      lastPrice: '',
      lastSize: '',
      lastSizeMicro: 0n,
      lastTotal: '',
      lastWidth: '',
      lastMid: '',
      lastMark: '',
      own: undefined,
    })
  }
  return out
}

// ---------------------------------------------------------------------------

/** One side of the book by price: its prices best-first, sizes and cumulative depth. */
interface SideIndex {
  cents: number[]
  size: Map<number, string>
  total: Map<number, bigint>
}

/**
 * Cumulative depth per level, as exact integers, **from the touch outwards**.
 *
 * **Not `+=` on `Number(size)`.** Sizes are 6dp decimal strings and summing them
 * as doubles produced `"12253.746224999999"` on the first real ladder this
 * component ever saw — twelve decimal places on a six-decimal field, which
 * `formatShares` correctly refused to render. Invariant 6 is not only about
 * balances.
 *
 * Both sides arrive best-first (`BookState` and `downLadder()` alike), so the
 * running total starts at index 0 on each — the best level's total is its own
 * size, wherever the layout then puts it on screen.
 */
function indexSide(levels: readonly BookLevelRow[]): SideIndex {
  const cents = new Array<number>(levels.length)
  const size = new Map<number, string>()
  const total = new Map<number, bigint>()
  let running = 0n
  for (let i = 0; i < levels.length; i++) {
    const level = levels[i] as BookLevelRow
    running += sharesOf(level.size)
    cents[i] = level.price_cents
    size.set(level.price_cents, level.size)
    total.set(level.price_cents, running)
  }
  return { cents, size, total }
}

/**
 * Paint one slot.
 *
 * **Each cell is guarded by its own last-painted string.** Guarding them all on
 * the size alone was a live defect: a slot holds whatever level the layout puts
 * in it, so a slot that received a new level whose size rendered identically
 * kept the previous level's price. The reference maker quotes a flat ladder
 * where every rung is the same size, which makes that the ordinary case — the
 * ladder showed `57, 57` with `58` missing.
 */
function paintSlot(
  h: SlotHandles,
  slot: Slot,
  ctx: { side: SideIndex; maxTotal: bigint; spread: string; own: OwnBook; outcome: Outcome },
): void {
  const kindChanged = h.lastKind !== slot.kind
  if (kindChanged) {
    h.root.dataset.kind = slot.kind
    // The spread is the mid, not a level: the browser suite reads ten
    // `[data-row]` above it and ten below.
    h.root.toggleAttribute('data-row', slot.kind !== 'spread')
    h.root.toggleAttribute('data-mid', slot.kind === 'spread')
    h.lastKind = slot.kind
  }

  writeText(h, 'mid', slot.kind === 'spread' ? ctx.spread : '')

  let price = ''
  let size = ''
  let sizeMicro = 0n
  let total = ''
  let width = '0'
  let ownLevel: OwnLevel | undefined
  if ((slot.kind === 'ask' || slot.kind === 'bid') && slot.cents !== null) {
    price = `${slot.cents}`
    const raw = slot.ghost ? undefined : ctx.side.size.get(slot.cents)
    if (raw !== undefined) {
      const depth = ctx.side.total.get(slot.cents) ?? 0n
      size = formatShares(raw)
      sizeMicro = sharesOf(raw)
      total = formatShares(formatFixed(depth, SHARES_DP))
      // A percentage is a pixel, and a pixel is the one place a float is the
      // right type. The ratio is taken after both sides are exact.
      width =
        ctx.maxTotal === 0n ? '0' : `${Math.min(100, Number((depth * 1000n) / ctx.maxTotal) / 10)}%`
    }
    ownLevel = ownLevelFor(ctx.own, ctx.outcome, slot.kind, slot.cents)
  }

  writeText(h, 'price', price)
  writeText(h, 'total', total)
  if (width !== h.lastWidth) {
    h.bar.style.width = width
    h.lastWidth = width
  }
  // The flash rule, exactly: compare the **rendered** value, never the payload.
  // A frame that re-sends `"1250.000000"` as `"1250.0"` renders the same string
  // and must not flash, or a quiet market strobes on every heartbeat
  // (components.md §4.3). Keyed on the size alone — a row whose price merely
  // shifted did not trade, and at a boundary every row shifts at once — and
  // never across a change of kind, which is the layout moving rather than the
  // level trading.
  if (size !== h.lastSize) {
    h.size.textContent = size
    if (h.lastSize !== '' && size !== '' && !kindChanged) flash(h.root, sizeMicro > h.lastSizeMicro)
    h.lastSize = size
    h.lastSizeMicro = sizeMicro
  }

  const working = ownLevel ? ownLevel.micro + ownLevel.pendingMicro : 0n
  const state =
    working === 0n ? ''
    : ownLevel && ownLevel.micro === 0n ? 'pending'
    : 'live'
  const markKey = state === '' ? '' : `${state}:${price}`
  if (markKey !== h.lastMark) {
    h.mark.hidden = state === ''
    // Pending has no order id yet, so its ✕ is refused with a sentence rather
    // than cancelling; dimmed, never a warning (components.md §4.5).
    if (state === 'pending') h.mark.dataset.pending = 'true'
    else delete h.mark.dataset.pending
    if (state === '') h.mark.removeAttribute('aria-label')
    else h.mark.setAttribute('aria-label', `Your order at ${price}¢ — cancel`)
    h.lastMark = markKey
  }
  h.own = state === '' ? undefined : ownLevel
}

function writeText(h: SlotHandles, cell: 'price' | 'total' | 'mid', text: string): void {
  const key = cell === 'price' ? 'lastPrice' : cell === 'total' ? 'lastTotal' : 'lastMid'
  if (h[key] === text) return
  h[cell].textContent = text
  h[key] = text
}

// ---------------------------------------------------------------------------

function hideTip(tip: HTMLElement): void {
  if (!tip.hidden) tip.hidden = true
}

/** The tooltip's faces: the order card, the action line, or the card above the line. */
function fillTip(tip: HTMLElement, level: OwnLevel | undefined, text: string | null): void {
  const face = level && text ? 'both' : level ? 'order' : 'action'
  if (tip.dataset.face !== face) tip.dataset.face = face
  if (level) fillOrderCard(tip, level)
  if (text) {
    const line = tip.querySelector<HTMLElement>('[data-tip=action]')
    if (line && line.textContent !== text) line.textContent = text
  }
}

/**
 * The order card: `Filled 2 / 5`, a bar, `3 remaining`.
 *
 * Over every order of the account's at this price, acknowledged and pending
 * alike — the mark stands for all of them and so does the ✕.
 */
function fillOrderCard(tip: HTMLElement, level: OwnLevel): void {
  const shares = (micro: bigint) => formatShares(formatFixed(micro, SHARES_DP))
  const filled = tip.querySelector<HTMLElement>('[data-tip=filled]')
  const bar = tip.querySelector<HTMLElement>('[data-tip=bar]')
  const remaining = tip.querySelector<HTMLElement>('[data-tip=remaining]')
  const left = level.micro + level.pendingMicro
  if (filled) filled.textContent = `${shares(level.filledMicro)} / ${shares(level.placedMicro)}`
  if (bar) {
    bar.style.width =
      level.placedMicro === 0n ? '0' : `${Math.min(100, Number((level.filledMicro * 1000n) / level.placedMicro) / 10)}%`
  }
  if (remaining) {
    remaining.textContent =
      level.micro === 0n ? `${shares(left)} sending…` : `${shares(left)} remaining`
  }
}

function sharesOf(size: string): bigint {
  try {
    return parseFixed(size, SHARES_DP)
  } catch {
    // An unparseable size is a frame the checksum will reject a moment later.
    // Counting it as zero keeps the ladder rendering until it does.
    return 0n
  }
}

function max(a: bigint, b: bigint): bigint {
  return a > b ? a : b
}

/**
 * Restart the flash animation on one slot.
 *
 * The class has to be able to re-fire while it is still running — a level that
 * ticks twice inside 420ms must flash twice — and CSS animations only restart on
 * a class change that survives a style recalculation. Reading `offsetWidth` is
 * the cheapest way to force one, and it happens only for cells that actually
 * changed, which in a normal frame is a handful rather than fifty.
 */
function flash(element: HTMLElement, up: boolean): void {
  const cls = up ? 'ud-flash-up' : 'ud-flash-down'
  element.classList.remove('ud-flash-up', 'ud-flash-down')
  void element.offsetWidth
  element.classList.add(cls)
}
